import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { CasingSearchState } from '../../../../shared/state/CasingSearchState';
import { WorkOrderService } from '../../../workorder/services/workorder.service';
import { WorkOrder } from '../../../workorder/components/model/workorder';
import { PaginatedWoResponse } from '../../../workorder/components/model/paginated-wo.model';
import { WorkOrderFiltersModel } from '../../../workorder/components/model/work-order-filters.model';
import { CasingSearchService } from '../../services/casing-search.services';
import { TransferCasingExistingWoResponse } from '../../model/transfer-casing-existing-wo.model';
import { UpdateCasingSearchResultsAction } from '../../../../shared/state/CasingSearchState';

@Component({
  selector: 'app-transfer-casing-workorder-screen',
  templateUrl: './transfer-casing-existing-workorder.component.html',
  styleUrls: ['./transfer-casing-existing-workorder.component.scss'],
  standalone: false
})
export class TransferCasingExistingWorkorderComponent implements OnInit {
  public store = inject(Store);
  public router = inject(Router);
  public translationService = inject(TranslationService);
  public workOrderService = inject(WorkOrderService);
  public fb = inject(FormBuilder);
  public alertService = inject(AlertService);
  public casingSearchService = inject(CasingSearchService);

  dealer: Dealer;
  userLogin: string;
  plantId: string;
  selectedCasings: any[] = [];
  selectedWorkOrder: WorkOrder | null = null;
  isTransferLoading = false;
  workOrderFilterForm: FormGroup;
  plantList = this.getPlantList();
  siteList = this.getSiteList();
  pageSizeOptions = [10, 20, 50, 100];
  compareById = (a, b) => a && b && a.id === b.id;
  readonly workOrderStatusPrefilters = ['RELEASED', 'IN_PROGRESS'];

  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<WorkOrder>;
  @ViewChild('customerModal') customerModal: ModalComponent;
  @ViewChild('customerModalContent') customerModalContent: TemplateRef<any>;

  tableConfig: TableConfig<WorkOrder> = this.buildTableConfig();

  constructor() {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.workOrderFilterForm = this.fb.group({
      workOrderNumber: [''],
      workOrderPlant: [this.plantList],
      workOrderSite: [this.siteList],
      customerCode: [''],
      customerName: ['']
    });
  }

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);
    this.selectedCasings = this.store.selectSnapshot(CasingSearchState.selectedCasings) || [];

    this.tableConfig = this.buildTableConfig();

    this.workOrderFilterForm.valueChanges.pipe(debounceTime(700), distinctUntilChanged()).subscribe((values) => {
      this.tableRef?.patchExtraFilters(values, true);
    });
  }

  clearSelections(): void {
    this.selectedWorkOrder = null;
    this.workOrderFilterForm.patchValue({
      workOrderNumber: '',
      workOrderPlant: this.plantList,
      workOrderSite: this.siteList,
      customerCode: '',
      customerName: ''
    });
    this.tableRef?.patchExtraFilters(this.workOrderFilterForm.value, true);
  }

  canTransfer(): boolean {
    return !!this.selectedWorkOrder && this.selectedCasings.length > 0;
  }

  onCancel(): void {
    this.router.navigate(['/home/search-casings/results']);
  }

  onTransfer(): void {
    if (!this.canTransfer()) {
      return;
    }

    const targetWorkOrder = this.selectedWorkOrder;
    if (!targetWorkOrder) {
      return;
    }

    const casingIds = this.selectedCasings.map((item) => item.workOrderLinePk).filter((casingId) => !!casingId);

    this.isTransferLoading = true;

    this.casingSearchService
      .transferCasingToExistingWo(casingIds, targetWorkOrder.id)
      .pipe(finalize(() => (this.isTransferLoading = false)))
      .subscribe({
        next: (response: TransferCasingExistingWoResponse) => {
          if (response.success) {
            this.alertService.showAlert({
              type: AlertType.success,
              content: `${this.translationService.translate('transferCasingToExistingWoSuccessText')} ${targetWorkOrder.workOrderNumber}`
            } as Alert);
            // Clear stale results so casing-search-result re-fetches on init
            this.store.dispatch(new UpdateCasingSearchResultsAction(null as any));
            this.router.navigate(['/home/search-casings']);
          } else {
            this.alertService.showAlert({
              type: AlertType.critical,
              content: `${this.translationService.translate('transferCasingToExistingWoFailureText')} ${targetWorkOrder.workOrderNumber}`
            } as Alert);
          }
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: `${this.translationService.translate('transferCasingToExistingWoFailureText')} ${targetWorkOrder.workOrderNumber}`
          } as Alert);
        }
      });
  }

  getWorkOrderFilters(): WorkOrderFiltersModel {
    const formValues = this.workOrderFilterForm.value;
    return {
      workOrderNumber: formValues.workOrderNumber || '',
      workOrderStatus: [...this.workOrderStatusPrefilters],
      workOrderPlant: Array.isArray(formValues.workOrderPlant)
        ? formValues.workOrderPlant.length > this.plantList.length - 1
          ? []
          : formValues.workOrderPlant.map((plant: any) => plant.id)
        : formValues.workOrderPlant || [],
      workOrderSite: Array.isArray(formValues.workOrderSite)
        ? formValues.workOrderSite.length > this.siteList.length - 1
          ? []
          : formValues.workOrderSite.map((site: any) => site.id)
        : formValues.workOrderSite || [],
      customerCode: formValues.customerCode || '',
      customerName: formValues.customerName || '',
      crn: (formValues.crn || '').trim(),
      workOrderReceivedDateStart: '',
      workOrderReceivedDateEnd: '',
      workOrderDueDateStart: '',
      workOrderDueDateEnd: '',
      searchByUser: this.userLogin || ''
    };
  }

  fetcher: PaginatorFetcher<WorkOrder> = (filters: PaginatorFilters) => {
    const workOrderFilters = this.getWorkOrderFilters();
    return this.workOrderService.searchWorkOrders(this.dealer.id, workOrderFilters, filters.page, filters.limit, filters.sort).pipe(
      map((response: PaginatedWoResponse) => {
        if (response && response.error && response.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return { items: [], count: 0 };
        }

        const items: WorkOrder[] = response?.data?.workorders || [];
        const count = response?.data?.pagination?.totalRecords || 0;
        return { items, count };
      })
    );
  };

  openCustomerModal(): void {
    this.customerModal.open({
      id: 'transfer-casing-customer-select',
      width: 800,
      title: this.translationService.translate('selectCustomerText'),
      content: this.customerModalContent,
      confirm: this.translationService.translate('closeText')
    });
  }

  onCustomerSelected(customer: any): void {
    if (!customer) {
      return;
    }

    this.workOrderFilterForm.patchValue({
      customerCode: customer.code,
      customerName: customer.customerName
    });

    if (this.customerModal?.dialog) {
      this.customerModal.dialog.closeAll();
    }
  }

  private buildTableConfig(): TableConfig<WorkOrder> {
    return {
      sort: {
        default: 'receivedDate:desc',
        resetPaginationOnSortChange: true
      },
      selection: {
        columnKeyForCheckboxLabel: 'workOrderNumber',
        allLabel: 'All Work Orders'
      },
      columns: {
        workOrderNumber: {
          cell: (item: WorkOrder) => item.workOrderNumber,
          header: this.translationService.translate('workOrderNumberText'),
          sortable: true
        },
        workOrderStatus: {
          cell: (item: WorkOrder) => item.workOrderStatus,
          header: this.translationService.translate('workOrderStatusText'),
          sortable: true
        },
        numberOfLines: {
          cell: (item: WorkOrder) => item.numberOfLines?.toString() || '0',
          header: this.translationService.translate('numberOfLinesText')
        },
        customerCode: {
          cell: (item: WorkOrder) => item.customerCode,
          header: this.translationService.translate('customerCodeText')
        },
        customerName: {
          cell: (item: WorkOrder) => item.customerName,
          header: this.translationService.translate('customerNameText')
        },
        crn: {
          cell: (item: WorkOrder) => item.crn,
          header: this.translationService.translate('CRNText')
        },
        plant: {
          cell: (item: WorkOrder) => item.plant,
          header: this.translationService.translate('plantText')
        },
        site: {
          cell: (item: WorkOrder) => item.site,
          header: this.translationService.translate('siteText')
        },
        receivedDate: {
          cell: (item: WorkOrder) => item.receivedDate || '',
          header: this.translationService.translate('receivedText'),
          sortable: true
        },
        dueDate: {
          cell: (item: WorkOrder) => item.dueDate || '',
          header: this.translationService.translate('dueText'),
          sortable: true
        }
      },
      paginator: {
        pageSize: 10,
        pageSizeOptions: this.pageSizeOptions,
        forceBottomAction: true,
        position: 'bottom',
        pageLabelOptions: {
          firstPageLabel: '',
          lastPageLabel: '',
          nextPageLabel: '',
          previousPageLabel: '',
          itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
        }
      },
      onSelectionChange: (selectedItems: WorkOrder[]) => {
        this.selectedWorkOrder = selectedItems.length === 1 ? selectedItems[0] : null;
      },
      onRowClick: () => undefined
    };
  }

  private getPlantList(): { id: string; displayName: string }[] {
    const plants = this.store.selectSnapshot(DealerState.plants);
    return plants?.length > 0
      ? plants.map((plant) => ({
          id: plant.id,
          displayName: plant.code + '-' + plant.name
        }))
      : [];
  }

  private getSiteList(): { id: string; displayName: string }[] {
    const sites = this.store.selectSnapshot(DealerState.sites);
    return sites?.length > 0
      ? sites.map((site) => ({
          id: site.id,
          displayName: site.code + '-' + site.name
        }))
      : [];
  }
}
